import { Tabs, Tooltip } from 'antd'
import { Horizontal, Vertical, Texto, useLocalStorage, MaterialIcon, GraviButton } from '@gravitate-js/excalibrr'
import { useStickyTab } from './hooks/useStickyTab'
import { SupplierConfigTab } from './SupplierConfigTab/SupplierConfigTab'
import { TerminalMappingTab } from './TerminalMappings/TerminalMappingTab'
import { ProductMappingTab } from './ProductMappings/ProductMappingTab'
import { useSupplierReconciliation } from '@api/useSupplierReconciliation'
import { useState } from 'react'
import { UnmappedSuppliersDrawer } from './components/UnmappedSuppliersDrawer'

const { TabPane } = Tabs

export function SupplierConfigs() {
  const { value: activeTab, setValue: setActiveTab } = useLocalStorage<string>(
    'supplier-config-sticky-tab',
    'system-settings'
  )
  const [drawerVisible, setDrawerVisible] = useState(false)

  // Fetch data to get unmapped suppliers and get API hooks
  const { useConfigOverview, useAddSupplierMapping } = useSupplierReconciliation()
  const { data, refetch } = useConfigOverview()
  const { mutateAsync: addMapping } = useAddSupplierMapping()

  // Get unmapped suppliers count
  const unmappedCount = data?.unmapped_external_supplier_names?.length || 0

  // Handler for mapping suppliers
  const handleMapSupplier = async (values: {
    source_name: string
    source_id?: string
    counterparty_id: string
  }) => {
    if (!values.counterparty_id) {
      throw new Error('Counterparty ID is required')
    }

    try {
      // Call the API to add the mapping
      await addMapping({
        body: {
          source_name: values.source_name,
          supplier_id: values.counterparty_id,
        },
      })

      // Explicitly refetch the data to update the UI
      await refetch()

      return true
    } catch (error) {
      console.error('Error mapping supplier:', error)
      throw error
    }
  }

  return (
    <Vertical border='all' borderRadius='6px' bg='default' style={{ height: '100%' }}>
      <Tabs
        activeKey={activeTab || undefined}
        onChange={setActiveTab}
        className='px-3'
        style={{ height: '100%', padding: 0 }}
        tabBarExtraContent={
          unmappedCount > 0 ? (
            <Tooltip title={'Click to manage unmapped suppliers.'}>
              <GraviButton
                type='default'
                error
                icon={<MaterialIcon icon='warning' className='pt-1' />}
                onClick={() => setDrawerVisible(true)}
                size='small'
                style={{ padding: '4px 12px' }}
              >
                <Horizontal gap={16} verticalCenter>
                  <Texto appearance='white' weight='bold' category='p2'>
                    {unmappedCount} suppliers are unmapped
                  </Texto>
                  <Texto className='pt-1' appearance='white' category='label'>
                    This will block supplier reconciliation for these suppliers.
                  </Texto>
                </Horizontal>
              </GraviButton>
            </Tooltip>
          ) : null
        }
      >
        <TabPane style={{ padding: 0 }} tab='Supplier Configuration' key='system-settings'>
          <SupplierConfigTab />
        </TabPane>
        <TabPane style={{ padding: 0 }} tab='Terminal Mappings' key='terminal-mappings'>
          <TerminalMappingTab />
        </TabPane>
        <TabPane style={{ padding: 0 }} tab='Product Mappings' key='product-mappings'>
          <ProductMappingTab />
        </TabPane>
      </Tabs>

      {/* Drawer for mapping unmapped suppliers */}
      {data?.unmapped_external_supplier_names && (
        <UnmappedSuppliersDrawer
          visible={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          unmappedSuppliers={data.unmapped_external_supplier_names}
          onMapSupplier={handleMapSupplier}
          counterpartyOptions={data?.rows || []}
        />
      )}
    </Vertical>
  )
}
